// Контракт API пульта (задача D1): вердикт «Готовности» и статус Анны для пилюли в сайдбаре.
//
// `GET /api/v1/status` → `Status`. Вердикт — одна строка «работает ли Анна»; его же отдаёт снимок «Готовности».
import { z } from 'zod';
import {
  BudgetStateSchema,
  ChannelModeSchema,
  DaySchema,
  MomentSchema,
  MonthSchema,
  StageSchema,
  type StatusTone,
} from './common.js';

/**
 * Вид вердикта (UX §1): `working` — работает; `economy` — экономит (лимит бюджета исчерпан); `broken` — не работает;
 * `stopped` — остановлена человеком; `not_configured` — ещё не настроена (ходов не было, а что-то мешает).
 */
export const VERDICT_KINDS = ['working', 'economy', 'broken', 'stopped', 'not_configured'] as const;
export const VerdictKindSchema = z.enum(VERDICT_KINDS);
export type VerdictKind = z.infer<typeof VerdictKindSchema>;

/** Вердикт словом — для точки статуса. */
export const VERDICT_WORD_RU: Readonly<Record<VerdictKind, string>> = {
  working: 'Работает',
  economy: 'Экономит',
  broken: 'Не работает',
  stopped: 'Остановлена',
  not_configured: 'Не настроена',
};

/** Тон точки вердикта: работает — синяя, экономит и не настроена — янтарная, не работает — красная, остановлена — серая. */
export const VERDICT_TONE: Readonly<Record<VerdictKind, StatusTone>> = {
  working: 'live',
  economy: 'human',
  not_configured: 'human',
  broken: 'error',
  stopped: 'reject',
};

/**
 * Вердикт: вид и слово; `headlineRu` — строка вердикта под шапкой «Готовности» (у «Работает» — нет); `shortRu` —
 * короткая строка для узкого экрана и пилюли; `since` — с какого момента вердикт этого вида держится.
 */
export const VerdictSchema = z.object({
  kind: VerdictKindSchema,
  wordRu: z.string(),
  headlineRu: z.string().nullable(),
  shortRu: z.string().nullable(),
  since: MomentSchema.nullable(),
});
export type Verdict = z.infer<typeof VerdictSchema>;

/** Канал в статусе: ключ (Ruling 21), подпись, режим канала из настроек и он же словами. */
export const StatusChannelSchema = z.object({
  key: z.string(),
  labelRu: z.string(),
  mode: ChannelModeSchema,
  modeRu: z.string(),
});
export type StatusChannel = z.infer<typeof StatusChannelSchema>;

/** Бюджет на модели: потрачено и лимит за день и месяц по МСК (`day`, `month`), рубли — до копейки. */
export const StatusBudgetSchema = z.object({
  state: BudgetStateSchema,
  day: DaySchema,
  month: MonthSchema,
  dayRub: z.number().nonnegative(),
  dayLimitRub: z.number().nonnegative(),
  monthRub: z.number().nonnegative(),
  monthLimitRub: z.number().nonnegative(),
});
export type StatusBudget = z.infer<typeof StatusBudgetSchema>;

/**
 * Статус Анны: вердикт, ступень запуска, кто и когда остановил (`null` — не остановлена или остановлена не из пульта),
 * каналы, бюджет, админы (кто чинит) коротко — «Влад Т.», и момент ответа.
 */
export const StatusSchema = z.object({
  verdict: VerdictSchema,
  stage: StageSchema,
  stopped: z.object({ at: MomentSchema, byName: z.string() }).nullable(),
  channels: z.array(StatusChannelSchema),
  budget: StatusBudgetSchema,
  admins: z.array(z.string()),
  at: MomentSchema,
});
export type Status = z.infer<typeof StatusSchema>;

/**
 * `GET /health` → `Health` (A13, T17): версия выпуска и возраст меток процессов в целых секундах — сердцебиения воркера
 * и пульса тест-бота; метки нет или она испорчена — `null`. Читают выкат и сторож (bash), поэтому имена полей не меняются.
 */
export const HealthSchema = z.object({
  release: z.string(),
  workerHeartbeatAgeSec: z.number().int().nonnegative().nullable(),
  testbotHeartbeatAgeSec: z.number().int().nonnegative().nullable(),
});
export type Health = z.infer<typeof HealthSchema>;
